import * as moment from 'moment-timezone';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Location } from '@angular/common';
import { HttpParameterCodec, HttpParams } from '@angular/common/http';
import { rowsToCsv } from './reporting-core/csv';

// Pure helpers live in the Angular-free reporting core (its README); re-exported
// here so existing `../utils` imports keep working.
import {
  propExists,
  propExistsAndIsNonEmpty,
  capitalizeFirstLetter,
  getDaysAndMonthsString,
  daysAgoText,
  getStaffNameDisplay,
  dateStringFromISO8601,
  timeStringFromISO8601,
  loadStringFilterFromGetParams,
  loadIntFilterFromGetParams,
  loadFloatFilterFromGetParams,
  loadBoolFilterFromGetParams,
  getISO8601DateString,
  getRelativeDateRangeBounds,
  resolveDateRangeBounds,
  getEventDetailsLink,
} from './reporting-core/utils';
import { getRelativeDateRangeLabel, RelativeDateRange } from './models/date-range-filter';
// Time-interval data lives in models/time-interval; re-exported for `../utils` callers.
import {
  TimeInterval,
  TimeIntervalDaily,
  TimeIntervalWeekly,
  TimeIntervalMonthly,
  TimeIntervals,
} from './models/time-interval';
export {
  TimeInterval,
  TimeIntervalDaily,
  TimeIntervalWeekly,
  TimeIntervalMonthly,
  TimeIntervals,
};
export {
  propExists,
  propExistsAndIsNonEmpty,
  capitalizeFirstLetter,
  getDaysAndMonthsString,
  daysAgoText,
  getStaffNameDisplay,
  dateStringFromISO8601,
  timeStringFromISO8601,
  loadStringFilterFromGetParams,
  loadIntFilterFromGetParams,
  loadFloatFilterFromGetParams,
  loadBoolFilterFromGetParams,
  getISO8601DateString,
  getRelativeDateRangeBounds,
  resolveDateRangeBounds,
  getEventDetailsLink,
};


/**
 * "Last edited {relative time} by {who}" — the saved-item pickers' row subtitle.
 * For the absolute form on an edit page, use `<app-last-updated-by-footer>`.
 */
export function lastEditedText(firstName: string | null,
                               lastName: string | null,
                               email: string | null,
                               updatedAt: string): string {
  const editor = getStaffNameDisplay(firstName, lastName, email);
  return 'Last edited ' + moment(updatedAt).fromNow() + ' by ' + editor;
}


/**
 * The user-facing message for a failed modal save/remove request: the
 * permission message on a 403, else the backend's `useful_error_message` or
 * `error_message` (both keys are in wide use), else the caller's fallback.
 */
export function modalErrorMessage(reason: any,
                                  permissionMessage: string,
                                  fallback: string): string {
  if (reason && reason.status === 403) {
    return permissionMessage;
  }
  const errorBody = (reason && reason.error) || {};
  const message = errorBody.useful_error_message || errorBody.error_message;
  return message || fallback;
}


/**
 * The birthday formats a bulk-upload spreadsheet may use, parsed STRICTLY and
 * year-first only: moment's non-strict parser reads a month-first `06/05/2010`
 * as `2006-05-20`, a plausible wrong birthday rather than a rejection (PRO-1335).
 */
const BIRTHDAY_INPUT_FORMATS = [
  'YYYY-MM-DD',
  'YYYY-M-D',
  'YYYY/MM/DD',
  'YYYY/M/D',
  'YYYY.MM.DD',
  'YYYY.M.D',
  'YYYYMMDD'
];


/**
 * Validate a bulk-upload birthday and normalize it to `YYYY-MM-DD`: the backend
 * splits on `-` and rejects any other separator (PRO-1335).
 *
 * @throws TypeError - If the birthday is missing, unparseable, or out of range.
 */
export function normalizeBirthdayInput(birthdayInput: string): string {
  if (!birthdayInput) {
    throw TypeError('Missing birthday.');
  }

  const birthdayMoment = moment(birthdayInput, BIRTHDAY_INPUT_FORMATS, true);
  if (!birthdayMoment.isValid()) {
    throw TypeError('Invalid birthday. Please ensure the birthday is in the format YYYY-MM-DD.');
  }
  if (!birthdayMoment.isBefore()) {
    throw TypeError('Birthday must be in the past.');
  }
  if (birthdayMoment.isBefore(moment().subtract(120, 'years'))) {
    throw TypeError('Birthday may not be more than 120 years ago.');
  }

  return birthdayMoment.format('YYYY-MM-DD');
}


/**
 * A bulk-upload row the server refused. `rowNumber` is the spreadsheet row —
 * 1-based, counting the header — matching the parse-time "Row N is invalid" wording.
 */
export class BulkUploadFailure {
  rowNumber: number;
  name: string;
  message: string;
}


/**
 * Order bulk-upload failures by spreadsheet row, in place. Rows upload in
 * concurrent batches, so failures arrive in server-answer order (PRO-1335).
 */
export function sortBulkUploadFailures(failures: BulkUploadFailure[]): BulkUploadFailure[] {
  return failures.sort((a, b) => a.rowNumber - b.rowNumber);
}


/**
 * Angular doesn't encode plus signs, so create a custom encoder which uses the
 * standard javascript encodeURIComponent function.
 */
export class HttpUrlEncodingCodec implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

/**
 * Apply an asynchronous function to a list of objects in discrete batches.
 *
 * @typeparam T - The type of the input objects.
 * @typeparam U - The type of the result objects.
 * @param batchSize - The number of concurrent function executions in each batch.
 * @param items - The array of input objects on which the asynchronous function will
 *     be executed.
 * @param promiseFunc - The asynchronous function which will be applied to each item.
 * @return - A promise which will resolve into an array of result objects once the
 *     asynchronous function has been applied to all input objects.
 */
export function batchAll<T, U>(batchSize: number,
                               items: T[],
                               promiseFunc: (arg0: T) => Promise<U>): Promise<U[]> {
  // Result accumulator.
  const results: U[] = [];

  // Create a copy of the items, so that the original input list is not modified.
  const itemsCopy = [...items];

  // Recursively accumulate batches of results until there
  // are no more items to process.
  const processNextBatch = (remainingItems: T[]): Promise<U[]> => {
    if (remainingItems.length === 0) {
      return Promise.resolve(results);
    }

    // Kick off the next batch of items to process.
    const itemPromises: Array<Promise<U>> = remainingItems
      .splice(0, batchSize)
      .map(promiseFunc);

    // Once this batch of items completes, recursively
    // process the next batch.
    return Promise.all(itemPromises)
      .then((nextResults) => {
        Array.prototype.push.apply(results, nextResults);
        return processNextBatch(remainingItems);
      });
  };

  return processNextBatch(itemsCopy);
}


/**
 * Move an element within an array.
 *
 * @param arr - The array to modify.
 * @param fromIndex - The initial index of the element.
 * @param toIndex - The final index of the element.
 */
export function arrayMove(arr, fromIndex, toIndex) {
  arr.splice(
    toIndex,
    0,
    arr.splice(fromIndex, 1)[0]
  );
}


/**
 * Represents the possible callback result statuses.
 */
export enum CallbackResultStatus {
  success = 'success',
  error = 'error'
}


/**
 * A class representing a callback function result.
 */
export class CallbackResult {
  text: string;
  status: CallbackResultStatus;
}

/** The weekday name of an iso8601 datetime (or date) string. */
export function dayOfWeekStringFromISO8601(iso8601datetime: string, timezone: string | null = null) {
  let parsedDt = moment(iso8601datetime);
  if (timezone !== null) { parsedDt = parsedDt.tz(timezone); }
  return parsedDt.format('dddd');
}

/**
 * Display the given iso8601 time string as a locally-formatted time
 * (hours and minutes, not seconds).
 *
 * We add a fake date in order for moment to parse the string.
 */
export function timeStringFromISO8601TimeOnly(iso8601time: string,
                                              addMinutes: number | null = null) {
  const thisMoment = moment('2022-05-19T' + iso8601time);
  if (addMinutes) {
    thisMoment.add(addMinutes, 'minutes');
  }
  return thisMoment.format('LT');
}

export function getISO8601DateStringFromMoment(date: moment.Moment) {
  return date.format().split('T')[0];
}

/** Whole years old now, or on `asOfDate` (`YYYY-MM-DD`). */
export function getAge(iso8601date: string, asOfDate?: string): number {
  return (asOfDate ? moment(asOfDate) : moment()).diff(iso8601date, 'years');
}

export function uncapitalizeFirstLetter(inputStr: string) {
  return inputStr.charAt(0).toLowerCase() + inputStr.slice(1);
}

/**
 * Get a random negative ID value, used to represent an object which has not
 * yet been saved to the API.
 */
export function getRandomNegativeId() {
  return Math.floor(
    -1 - (Math.random() * Math.pow(2, 30))
  );
}

export function getRandomHexString(numDigits: number): string {
  let output = '';
  const characterSet = '0123456789abcdef';

  for (let i = 0; i < numDigits; i++) {
    output += characterSet.charAt(
      Math.floor(Math.random() * characterSet.length)
    );
  }

  return output;
}

/**
 * Download the given content as a file with the given type and name.
 *
 * @param content - The file content, as a javascript Blob. The Blob must have
 *   a defined "type" parameter.
 * @param fileName - The name of the download file, including an extension.
 */
export function generateDownload(content: Blob, fileName: string) {
  // IE-specific; see http://msdn.microsoft.com/en-us/library/ie/hh779016.aspx
  if (window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveBlob(content, fileName);
  } else {
    // Generate a hidden link, and then 'click' on it, invoking an
    // immediate download for the user.
    const a = window.document.createElement('a');

    a.href = window.URL.createObjectURL(content);
    a.download = fileName;
    a.type = content.type;

    document.body.appendChild(a);
    a.click();
    // Remove the hidden link element.
    document.body.removeChild(a);
  }
}

/**
 * Download the given content as a CSV file with the given name.
 *
 * @param content - A list of rows, each of which is in turn a list of strings.
 * @param fileName - The name of the download file, including an extension.
 */
export function generateCsvDownload(content: any[][], fileName: string) {
  // A UTF-8 BOM (U+FEFF) so Excel on Windows detects the encoding instead of
  // showing mojibake; `rowsToCsv` deliberately emits none (see its header).
  const utf8Bom = String.fromCharCode(0xfeff);
  generateDownload(
    new Blob(
      [utf8Bom, rowsToCsv(content)],
      {type: 'text/csv;charset=utf-8'}
      ),
    fileName
  );
}

export function objHasNonEmptyValues(obj: { [index: string]: any }) {
  if (!propExists(obj)) { return false; }

  for (const key of Object.keys(obj)) {
    if (propExists(obj[key]) && obj[key].length > 0) { return true; }
  }

  return false;
}

export function searchList<T>(searchInput: string, options: { value: T, searchableText: string }[]): T[] {
  const transformedInput = searchInput.trim().toLowerCase();

  if (transformedInput === '') { return options.map(option => option.value); }

  const exactMatchOptions: { value: T, searchableText: string }[] = [];
  const partialMatchOptions: { value: T, searchableText: string }[] = [];

  for (const option of options) {
    const transformedOption = option.searchableText.trim().toLowerCase();

    if (transformedOption === transformedInput) {
      exactMatchOptions.push(option);
    } else if (transformedOption.indexOf(transformedInput) >= 0) {
      partialMatchOptions.push(option);
    }
  }

  return exactMatchOptions.concat(partialMatchOptions).map(option => option.value);
}

export function searchStringList(searchInput: string, options: string[]): string[] {
  return searchList(
    searchInput,
    options.map((option) => { return { value: option, searchableText: option}; })
  );
}

/**
 * The GET-parameter object for a set of filters: every non-empty filter (plus
 * `filtersToSaveEmpty` as ''), minus `filtersToIgnore`. Pure, so a link to a
 * different URL can reuse the exact serialization `addFiltersToGetParams` writes.
 */
export function buildFiltersGetParams(filters: any,
                                      filtersToSaveEmpty?: string[],
                                      filtersToIgnore?: string[]): {[key: string]: any} {
  const filtersToAdd: any = {};

  for (const filterKey in filters) {
    if (!filters.hasOwnProperty(filterKey)) { continue; }
    const willSave = (
      (
        propExistsAndIsNonEmpty(filters[filterKey]) ||
        (
          propExists(filtersToSaveEmpty) &&
          filtersToSaveEmpty.includes(filterKey)
        )
      ) &&
      (
        !propExists(filtersToIgnore) ||
        !filtersToIgnore.includes(filterKey)
      )
    );
    if (willSave) {
      filtersToAdd[filterKey] = propExists(filters[filterKey]) ? filters[filterKey] : '';
    }
  }

  return filtersToAdd;
}

/**
 * Add the given filters to the page's GET parameters, so they may be extracted
 * later (for example, after reloading the page).
 */
export function addFiltersToGetParams(filters: any,
                                      router: Router,
                                      activatedRoute: ActivatedRoute,
                                      filtersToSaveEmpty?: string[],
                                      filtersToIgnore?: string[]) {
  router.navigate(
    [],
    {
      relativeTo: activatedRoute,
      queryParams: buildFiltersGetParams(filters, filtersToSaveEmpty, filtersToIgnore),
      replaceUrl: true
    }
  );
}

export function getFilterTextForDateRange(filterName: string, minDate?: string, maxDate?: string) {
  if (propExistsAndIsNonEmpty(minDate) && propExistsAndIsNonEmpty(maxDate)) {
    if (minDate === maxDate) {
      return filterName + ' on ' + dateStringFromISO8601(minDate);
    } else {
      return filterName + ' between ' + dateStringFromISO8601(minDate) + ' and ' + dateStringFromISO8601(maxDate);
    }
  } else if (propExistsAndIsNonEmpty(minDate)) {
    return filterName + ' on or after ' + dateStringFromISO8601(minDate);
  } else if (propExistsAndIsNonEmpty(maxDate)) {
    return filterName + ' on or before ' + dateStringFromISO8601(maxDate);
  } else {
    return null;
  }
}

/**
 * Active-filter text for a date-range filter (PRO-1248). Precedence matches the
 * backend: fixed min/max, then days ago, then a preset range.
 */
export function getActiveDateRangeFilterText(
  filterName: string,
  minDate?: string,
  maxDate?: string,
  range?: string,
  daysMin?: number,
  daysMax?: number,
): string | null {
  if (propExistsAndIsNonEmpty(minDate) || propExistsAndIsNonEmpty(maxDate)) {
    return getFilterTextForDateRange(filterName, minDate, maxDate);
  }
  if (propExists(daysMin) && propExists(daysMax)) {
    if (daysMin === daysMax) {
      return filterName + ': exactly ' + daysAgoText(daysMin);
    }
    return filterName + ': between ' + daysMin.toString() + ' and ' + daysAgoText(daysMax);
  }
  if (propExists(daysMin)) {
    return filterName + ': at least ' + daysAgoText(daysMin);
  }
  if (propExists(daysMax)) {
    return filterName + ': at most ' + daysAgoText(daysMax);
  }
  if (propExistsAndIsNonEmpty(range)) {
    return filterName + ': ' + getRelativeDateRangeLabel(range);
  }
  return null;
}

/**
 * The interval a chart report seeds when a relative range is picked (mirrors
 * the `<app-time-range-sidebar-filter>` presets); null for a range with no opinion.
 */
export function getDefaultIntervalForRelativeRange(rangeKey: string): TimeInterval | null {
  switch (rangeKey) {
    case RelativeDateRange.thisQuarter:
    case RelativeDateRange.lastQuarter:
    case RelativeDateRange.last90Days:
      return TimeIntervalWeekly;
    case RelativeDateRange.thisYear:
    case RelativeDateRange.lastYear:
    case RelativeDateRange.yearToDate:
    case RelativeDateRange.last365Days:
      return TimeIntervalMonthly;
    case RelativeDateRange.today:
    case RelativeDateRange.yesterday:
    case RelativeDateRange.last24Hours:
    case RelativeDateRange.last7Days:
    case RelativeDateRange.last28Days:
    case RelativeDateRange.last31Days:
    case RelativeDateRange.thisWeekStartingMonday:
    case RelativeDateRange.thisWeekStartingSunday:
    case RelativeDateRange.lastWeekStartingMonday:
    case RelativeDateRange.lastWeekStartingSunday:
    case RelativeDateRange.thisMonth:
    case RelativeDateRange.lastMonth:
      return TimeIntervalDaily;
    default:
      return null;
  }
}

export function getUrlHashDerivedFromText(text: string) {
  return text
    .trim()
    .toLowerCase()
    .replace(/ /g, '-')
    .replace(/[^a-zA-Z0-9-]+/g, '');
}

/**
 * Used to allow ngModel for native types in lists (e.g., to have an input
 * element for each string in a JSON list).
 */
export function trackByIdx(index: number, obj: any): any {
  return index;
}

export function getHistogramRanges(xValues: number[],
                                   numBins: number,
                                   maxUpperBound: number): number[][] {
  if (xValues.length === 0) { return []; }

  let rangeTop = xValues[xValues.length - 1];

  if (rangeTop <= 0) { return [[0, 0]]; }

  const rangeTopRemainder = rangeTop % numBins;
  if (rangeTopRemainder > 0) {
    rangeTop += numBins - rangeTopRemainder;
  }

  const binSize = rangeTop / numBins;

  const bins: number[][] = [];

  for (let binMin = 0; binMin < rangeTop; binMin += binSize) {
    bins.push([binMin + 1, binMin + binSize]);
  }

  return bins;
}

export function getHttpParamsForFilters(filters: any) {
  let params = new HttpParams({encoder: new HttpUrlEncodingCodec() });

  if (filters) {
    for (const filterKey of Object.keys(filters)) {
      const value = filters[filterKey];
      if (value == null) { continue; }

      if (Array.isArray(value)) {
        // Comma-joined, and an EMPTY array is dropped: `?x=` 400s on a
        // filterset that validates its values. Server-side twin:
        // `_filters_to_query_params` in capitan-server `emailed_report_helpers.py`
        // — change both together or emailed reports diverge from the live page.
        if (value.length === 0) { continue; }
        params = params.append(filterKey, value.join(','));
      } else {
        params = params.append(filterKey, value);
      }
    }
  }

  return params;
}

export function scrollToElement(id: string) {
  document.querySelector('#' + id).scrollIntoView();
}

export function getDiscountDisplayName(discount: {name: string, code: string}): string {
  if (propExistsAndIsNonEmpty(discount.name)) {
    let output = discount.name;
    if (propExistsAndIsNonEmpty(discount.code)) {
      output += ' (' + discount.code + ')';
    }
    return output;
  } else {
    return discount.code;
  }
}

export function getTimeZoneDisplayName(timeZoneName: string) {
  if (TimeZoneDisplayNames[timeZoneName] !== undefined) {
    return TimeZoneDisplayNames[timeZoneName];
  } else {
    return timeZoneName.split('/').join(' ');
  }
}

export function getBrowserTimeZone() {
  return moment.tz.guess();
}

export function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function replaceUrlQueryParams(newQueryParams: any,
                                      location: Location,
                                      router: Router,
                                      activatedRoute: ActivatedRoute) {
  location.replaceState(
    router.serializeUrl(
      router.createUrlTree(
        [],
        {
          relativeTo: activatedRoute,
          queryParams: newQueryParams
        }
      )
    )
  );
}

export function removeDuplicates<T>(array: T[]): T[] {
    return [...new Set(array)];
}

export function deleteProp(obj: any, prop: string) {
  delete obj[prop];
}

export function hasPropNotInList(obj: any, propList: string[]) {
  return Object.keys(obj).some(
    key => !propList.includes(key) && (obj[key] !== undefined)
  );
}

/**
 * Whether the loaded filters carry something the user asked for, as opposed
 * to defaults the loader seeds: a seeded key counts only when the URL names it.
 */
export function hasUserAppliedFilters(filters: any, queryParamMap: ParamMap,
                                      excludedProps: string[], seededProps: string[]): boolean {
  return hasPropNotInList(filters, [
    ...excludedProps,
    ...seededProps.filter(key => !queryParamMap.has(key)),
  ]);
}

export function addPropsToObject(sourceObj: any, destObj: any) {
  for (const key of Object.keys(sourceObj)) {
    if (sourceObj[key] !== undefined) {
      destObj[key] = sourceObj[key];
    }
  }
}

export function isLightColor(colorHex: string) {
  colorHex = colorHex.toLowerCase();

  const red = parseInt(colorHex.slice(0, 2), 16);
  const green = parseInt(colorHex.slice(2, 4), 16);
  const blue = parseInt(colorHex.slice(4, 6), 16);

  const lum = (0.299 * red) + (0.587 * green) + (0.114 * blue);

  return lum > 125;
}

/**
 * Obtain a the given parameter's value from the given object using
 * case-insensitive key matching.
 *
 * Taken from: https://stackoverflow.com/a/47538066
 */
export function getParameterCaseInsensitive(object: any, key: string) {
  const keyLowercase = key.toLowerCase();
  return object[Object.keys(object).filter((k)=> {
    return k.toLowerCase() === keyLowercase;
  })[0]];
}

/**
 * Represents a color with a name and RGB hex value.
 */
export class NamedColor {
  name: string;
  colorHex: string;
}

/**
 * The 'Capitan Teal' standard color.
 */
export const StandardColorTeal: NamedColor = {
  name: 'Teal',
  colorHex: '00b3a7'
};

/**
 *  Default for alerts.
 */
export const StandardColorRed: NamedColor = {
  name: 'Red',
  colorHex: 'ff0000'
};

/**
 * The list of standard colors.
 */
export const StandardColors: NamedColor[] = [
  StandardColorRed,
  {
    name: 'Dark Red',
    colorHex: '8b0000'
  },
  {
    name: 'Orange',
    colorHex: 'ffa500'
  },
  {
    name: 'Yellow',
    colorHex: 'ffff00'
  },
  {
    name: 'Green',
    colorHex: '008000'
  },
  {
    name: 'Lime',
    colorHex: '00ff00'
  },
  StandardColorTeal,
  {
    name: 'Blue',
    colorHex: '0000ff'
  },
  {
    name: 'Navy',
    colorHex: '000080'
  },
  {
    name: 'Aquamarine',
    colorHex: '7fffd4'
  },
  {
    name: 'Purple',
    colorHex: '800080'
  },
  {
    name: 'Plum',
    colorHex: 'dda0dd'
  },
  {
    name: 'Pink',
    colorHex: 'ffc0cb'
  },
  {
    name: 'Deep Pink',
    colorHex: 'ff1493'
  },
  {
    name: 'Brown',
    colorHex: 'a52a2a'
  },
  {
    name: 'Khaki',
    colorHex: 'bdb76b'
  },
  {
    name: 'White',
    colorHex: 'ffffff'
  },
  {
    name: 'Black',
    colorHex: '000000'
  },
  {
    name: 'Gray',
    colorHex: '808080'
  }
];

export enum ChartType {
  line = 'line',
  bar = 'bar'
}

export class Month {
  name: string;
  number: number;
}
export const Months: Month[] = [
  {name: 'January', number: 1},
  {name: 'February', number: 2},
  {name: 'March', number: 3},
  {name: 'April', number: 4},
  {name: 'May', number: 5},
  {name: 'June', number: 6},
  {name: 'July', number: 7},
  {name: 'August', number: 8},
  {name: 'September', number: 9},
  {name: 'October', number: 10},
  {name: 'November', number: 11},
  {name: 'December', number: 12}
];

export const TimeZoneDisplayNames = {
  'America/Denver': 'US Mountain',
  'America/Los_Angeles': 'US Pacific',
  'America/New_York': 'US Eastern',
  'America/Phoenix': 'Arizona',
  'America/Toronto': 'CA Eastern',
  'America/Vancouver': 'CA Pacific',
  'Australia/Perth': 'AU Western',
  'Canada/Eastern': 'CA Eastern',
  'Europe/London': 'UK',
  'Europe/Stockholm': 'Sweden',
  'US/Central': 'US Central',
  'US/Eastern': 'US Eastern',
  'US/Mountain': 'US Mountain',
  'US/Pacific': 'US Pacific'
};

export const TimeZones = ['Africa/Abidjan', 'Africa/Accra', 'Africa/Addis Ababa', 'Africa/Algiers', 'Africa/Asmara', 'Africa/Bamako', 'Africa/Bangui', 'Africa/Banjul', 'Africa/Bissau', 'Africa/Blantyre', 'Africa/Brazzaville', 'Africa/Bujumbura', 'Africa/Cairo', 'Africa/Casablanca', 'Africa/Ceuta', 'Africa/Conakry', 'Africa/Dakar', 'Africa/Dar es Salaam', 'Africa/Djibouti', 'Africa/Douala', 'Africa/El Aaiun', 'Africa/Freetown', 'Africa/Gaborone', 'Africa/Harare', 'Africa/Johannesburg', 'Africa/Juba', 'Africa/Kampala', 'Africa/Khartoum', 'Africa/Kigali', 'Africa/Kinshasa', 'Africa/Lagos', 'Africa/Libreville', 'Africa/Lome', 'Africa/Luanda', 'Africa/Lubumbashi', 'Africa/Lusaka', 'Africa/Malabo', 'Africa/Maputo', 'Africa/Maseru', 'Africa/Mbabane', 'Africa/Mogadishu', 'Africa/Monrovia', 'Africa/Nairobi', 'Africa/Ndjamena', 'Africa/Niamey', 'Africa/Nouakchott', 'Africa/Ouagadougou', 'Africa/Porto-Novo', 'Africa/Sao Tome', 'Africa/Tripoli', 'Africa/Tunis', 'Africa/Windhoek', 'America/Adak', 'America/Anchorage', 'America/Anguilla', 'America/Antigua', 'America/Araguaina', 'America/Argentina/Buenos Aires', 'America/Argentina/Catamarca', 'America/Argentina/Cordoba', 'America/Argentina/Jujuy', 'America/Argentina/La Rioja', 'America/Argentina/Mendoza', 'America/Argentina/Rio Gallegos', 'America/Argentina/Salta', 'America/Argentina/San Juan', 'America/Argentina/San Luis', 'America/Argentina/Tucuman', 'America/Argentina/Ushuaia', 'America/Aruba', 'America/Asuncion', 'America/Atikokan', 'America/Bahia', 'America/Bahia Banderas', 'America/Barbados', 'America/Belem', 'America/Belize', 'America/Blanc-Sablon', 'America/Boa Vista', 'America/Bogota', 'America/Boise', 'America/Cambridge Bay', 'America/Campo Grande', 'America/Cancun', 'America/Caracas', 'America/Cayenne', 'America/Cayman', 'America/Chicago', 'America/Chihuahua', 'America/Costa Rica', 'America/Creston', 'America/Cuiaba', 'America/Curacao', 'America/Danmarkshavn', 'America/Dawson', 'America/Dawson Creek', 'America/Denver', 'America/Detroit', 'America/Dominica', 'America/Edmonton', 'America/Eirunepe', 'America/El Salvador', 'America/Fort Nelson', 'America/Fortaleza', 'America/Glace Bay', 'America/Goose Bay', 'America/Grand Turk', 'America/Grenada', 'America/Guadeloupe', 'America/Guatemala', 'America/Guayaquil', 'America/Guyana', 'America/Halifax', 'America/Havana', 'America/Hermosillo', 'America/Indiana/Indianapolis', 'America/Indiana/Knox', 'America/Indiana/Marengo', 'America/Indiana/Petersburg', 'America/Indiana/Tell City', 'America/Indiana/Vevay', 'America/Indiana/Vincennes', 'America/Indiana/Winamac', 'America/Inuvik', 'America/Iqaluit', 'America/Jamaica', 'America/Juneau', 'America/Kentucky/Louisville', 'America/Kentucky/Monticello', 'America/Kralendijk', 'America/La Paz', 'America/Lima', 'America/Los Angeles', 'America/Lower Princes', 'America/Maceio', 'America/Managua', 'America/Manaus', 'America/Marigot', 'America/Martinique', 'America/Matamoros', 'America/Mazatlan', 'America/Menominee', 'America/Merida', 'America/Metlakatla', 'America/Mexico City', 'America/Miquelon', 'America/Moncton', 'America/Monterrey', 'America/Montevideo', 'America/Montserrat', 'America/Nassau', 'America/New York', 'America/Nipigon', 'America/Nome', 'America/Noronha', 'America/North Dakota/Beulah', 'America/North Dakota/Center', 'America/North Dakota/New Salem', 'America/Nuuk', 'America/Ojinaga', 'America/Panama', 'America/Pangnirtung', 'America/Paramaribo', 'America/Phoenix', 'America/Port-au-Prince', 'America/Port of Spain', 'America/Porto Velho', 'America/Puerto Rico', 'America/Punta Arenas', 'America/Rainy River', 'America/Rankin Inlet', 'America/Recife', 'America/Regina', 'America/Resolute', 'America/Rio Branco', 'America/Santarem', 'America/Santiago', 'America/Santo Domingo', 'America/Sao Paulo', 'America/Scoresbysund', 'America/Sitka', 'America/St Barthelemy', 'America/St Johns', 'America/St Kitts', 'America/St Lucia', 'America/St Thomas', 'America/St Vincent', 'America/Swift Current', 'America/Tegucigalpa', 'America/Thule', 'America/Thunder Bay', 'America/Tijuana', 'America/Toronto', 'America/Tortola', 'America/Vancouver', 'America/Whitehorse', 'America/Winnipeg', 'America/Yakutat', 'America/Yellowknife', 'Antarctica/Casey', 'Antarctica/Davis', 'Antarctica/DumontDUrville', 'Antarctica/Macquarie', 'Antarctica/Mawson', 'Antarctica/McMurdo', 'Antarctica/Palmer', 'Antarctica/Rothera', 'Antarctica/Syowa', 'Antarctica/Troll', 'Antarctica/Vostok', 'Arctic/Longyearbyen', 'Asia/Aden', 'Asia/Almaty', 'Asia/Amman', 'Asia/Anadyr', 'Asia/Aqtau', 'Asia/Aqtobe', 'Asia/Ashgabat', 'Asia/Atyrau', 'Asia/Baghdad', 'Asia/Bahrain', 'Asia/Baku', 'Asia/Bangkok', 'Asia/Barnaul', 'Asia/Beirut', 'Asia/Bishkek', 'Asia/Brunei', 'Asia/Chita', 'Asia/Choibalsan', 'Asia/Colombo', 'Asia/Damascus', 'Asia/Dhaka', 'Asia/Dili', 'Asia/Dubai', 'Asia/Dushanbe', 'Asia/Famagusta', 'Asia/Gaza', 'Asia/Hebron', 'Asia/Ho Chi Minh', 'Asia/Hong Kong', 'Asia/Hovd', 'Asia/Irkutsk', 'Asia/Jakarta', 'Asia/Jayapura', 'Asia/Jerusalem', 'Asia/Kabul', 'Asia/Kamchatka', 'Asia/Karachi', 'Asia/Kathmandu', 'Asia/Khandyga', 'Asia/Kolkata', 'Asia/Krasnoyarsk', 'Asia/Kuala Lumpur', 'Asia/Kuching', 'Asia/Kuwait', 'Asia/Macau', 'Asia/Magadan', 'Asia/Makassar', 'Asia/Manila', 'Asia/Muscat', 'Asia/Nicosia', 'Asia/Novokuznetsk', 'Asia/Novosibirsk', 'Asia/Omsk', 'Asia/Oral', 'Asia/Phnom Penh', 'Asia/Pontianak', 'Asia/Pyongyang', 'Asia/Qatar', 'Asia/Qostanay', 'Asia/Qyzylorda', 'Asia/Riyadh', 'Asia/Sakhalin', 'Asia/Samarkand', 'Asia/Seoul', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Srednekolymsk', 'Asia/Taipei', 'Asia/Tashkent', 'Asia/Tbilisi', 'Asia/Tehran', 'Asia/Thimphu', 'Asia/Tokyo', 'Asia/Tomsk', 'Asia/Ulaanbaatar', 'Asia/Urumqi', 'Asia/Ust-Nera', 'Asia/Vientiane', 'Asia/Vladivostok', 'Asia/Yakutsk', 'Asia/Yangon', 'Asia/Yekaterinburg', 'Asia/Yerevan', 'Atlantic/Azores', 'Atlantic/Bermuda', 'Atlantic/Canary', 'Atlantic/Cape Verde', 'Atlantic/Faroe', 'Atlantic/Madeira', 'Atlantic/Reykjavik', 'Atlantic/South Georgia', 'Atlantic/St Helena', 'Atlantic/Stanley', 'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Broken Hill', 'Australia/Currie', 'Australia/Darwin', 'Australia/Eucla', 'Australia/Hobart', 'Australia/Lindeman', 'Australia/Lord Howe', 'Australia/Melbourne', 'Australia/Perth', 'Australia/Sydney', 'Canada/Atlantic', 'Canada/Central', 'Canada/Eastern', 'Canada/Mountain', 'Canada/Newfoundland', 'Canada/Pacific', 'Europe/Amsterdam', 'Europe/Andorra', 'Europe/Astrakhan', 'Europe/Athens', 'Europe/Belgrade', 'Europe/Berlin', 'Europe/Bratislava', 'Europe/Brussels', 'Europe/Bucharest', 'Europe/Budapest', 'Europe/Busingen', 'Europe/Chisinau', 'Europe/Copenhagen', 'Europe/Dublin', 'Europe/Gibraltar', 'Europe/Guernsey', 'Europe/Helsinki', 'Europe/Isle of Man', 'Europe/Istanbul', 'Europe/Jersey', 'Europe/Kaliningrad', 'Europe/Kiev', 'Europe/Kirov', 'Europe/Lisbon', 'Europe/Ljubljana', 'Europe/London', 'Europe/Luxembourg', 'Europe/Madrid', 'Europe/Malta', 'Europe/Mariehamn', 'Europe/Minsk', 'Europe/Monaco', 'Europe/Moscow', 'Europe/Oslo', 'Europe/Paris', 'Europe/Podgorica', 'Europe/Prague', 'Europe/Riga', 'Europe/Rome', 'Europe/Samara', 'Europe/San Marino', 'Europe/Sarajevo', 'Europe/Saratov', 'Europe/Simferopol', 'Europe/Skopje', 'Europe/Sofia', 'Europe/Stockholm', 'Europe/Tallinn', 'Europe/Tirane', 'Europe/Ulyanovsk', 'Europe/Uzhgorod', 'Europe/Vaduz', 'Europe/Vatican', 'Europe/Vienna', 'Europe/Vilnius', 'Europe/Volgograd', 'Europe/Warsaw', 'Europe/Zagreb', 'Europe/Zaporozhye', 'Europe/Zurich', 'GMT', 'Indian/Antananarivo', 'Indian/Chagos', 'Indian/Christmas', 'Indian/Cocos', 'Indian/Comoro', 'Indian/Kerguelen', 'Indian/Mahe', 'Indian/Maldives', 'Indian/Mauritius', 'Indian/Mayotte', 'Indian/Reunion', 'Pacific/Apia', 'Pacific/Auckland', 'Pacific/Bougainville', 'Pacific/Chatham', 'Pacific/Chuuk', 'Pacific/Easter', 'Pacific/Efate', 'Pacific/Enderbury', 'Pacific/Fakaofo', 'Pacific/Fiji', 'Pacific/Funafuti', 'Pacific/Galapagos', 'Pacific/Gambier', 'Pacific/Guadalcanal', 'Pacific/Guam', 'Pacific/Honolulu', 'Pacific/Kiritimati', 'Pacific/Kosrae', 'Pacific/Kwajalein', 'Pacific/Majuro', 'Pacific/Marquesas', 'Pacific/Midway', 'Pacific/Nauru', 'Pacific/Niue', 'Pacific/Norfolk', 'Pacific/Noumea', 'Pacific/Pago Pago', 'Pacific/Palau', 'Pacific/Pitcairn', 'Pacific/Pohnpei', 'Pacific/Port Moresby', 'Pacific/Rarotonga', 'Pacific/Saipan', 'Pacific/Tahiti', 'Pacific/Tarawa', 'Pacific/Tongatapu', 'Pacific/Wake', 'Pacific/Wallis', 'US/Alaska', 'US/Arizona', 'US/Central', 'US/Eastern', 'US/Hawaii', 'US/Mountain', 'US/Pacific', 'UTC'];

export enum CountryIsoCodes {
    UNITED_STATES = 'US',
    UNITED_STATES_MINOR = 'UM',
    UNITED_KINGDOM = 'GB',
    AUSTRALIA = 'AU',
    CANADA = 'CA',
    THAILAND = 'TH',
}

export enum CountryNames {
    UNITED_STATES = 'United States',
    UNITED_STATES_MINOR = 'United State Minor Outlying Islands',
    UNITED_KINGDOM = 'United Kingdom',
    AUSTRALIA = 'Australia',
    CANADA = 'Canada',
    THAILAND = 'Thailand',
}

interface CountryWithCode {
  code: string;
  name: string; // ISO 3166 code
}

export const CountryCodes: CountryWithCode[] = [
  {code: 'AF', name: 'Afghanistan'},
  {code: 'AL', name: 'Albania'},
  {code: 'DZ', name: 'Algeria'},
  {code: 'AS', name: 'American Samoa'},
  {code: 'AD', name: 'Andorra'},
  {code: 'AO', name: 'Angola'},
  {code: 'AI', name: 'Anguilla'},
  {code: 'AQ', name: 'Antarctica'},
  {code: 'AG', name: 'Antigua And Barbuda'},
  {code: 'AR', name: 'Argentina'},
  {code: 'AM', name: 'Armenia'},
  {code: 'AW', name: 'Aruba'},
  {code: CountryIsoCodes.AUSTRALIA, name: CountryNames.AUSTRALIA},
  {code: 'AT', name: 'Austria'},
  {code: 'AZ', name: 'Azerbaijan'},
  {code: 'BS', name: 'Bahamas'},
  {code: 'BH', name: 'Bahrain'},
  {code: 'BD', name: 'Bangladesh'},
  {code: 'BB', name: 'Barbados'},
  {code: 'BY', name: 'Belarus'},
  {code: 'BE', name: 'Belgium'},
  {code: 'BZ', name: 'Belize'},
  {code: 'BJ', name: 'Benin'},
  {code: 'BM', name: 'Bermuda'},
  {code: 'BT', name: 'Bhutan'},
  {code: 'BO', name: 'Bolivia'},
  {code: 'BA', name: 'Bosnia and Herzegovina'},
  {code: 'BW', name: 'Botswana'},
  {code: 'BV', name: 'Bouvet Island'},
  {code: 'BR', name: 'Brazil'},
  {code: 'IO', name: 'British Indian Ocean Territory'},
  {code: 'BN', name: 'Brunei'},
  {code: 'BG', name: 'Bulgaria'},
  {code: 'BF', name: 'Burkina Faso'},
  {code: 'BI', name: 'Burundi'},
  {code: 'KH', name: 'Cambodia'},
  {code: 'CM', name: 'Cameroon'},
  {code: CountryIsoCodes.CANADA, name: CountryNames.CANADA},
  {code: 'CV', name: 'Cape Verde'},
  {code: 'KY', name: 'Cayman Islands'},
  {code: 'CF', name: 'Central African Republic'},
  {code: 'TD', name: 'Chad'},
  {code: 'CL', name: 'Chile'},
  {code: 'CN', name: 'China'},
  {code: 'CX', name: 'Christmas Island'},
  {code: 'CC', name: 'Cocos (Keeling) Islands'},
  {code: 'CO', name: 'Colombia'},
  {code: 'KM', name: 'Comoros'},
  {code: 'CG', name: 'Republic Of The Congo'},
  {code: 'CD', name: 'Democratic Republic Of The Congo'},
  {code: 'CK', name: 'Cook Islands'},
  {code: 'CR', name: 'Costa Rica'},
  {code: 'CI', name: 'Cote D\'Ivoire (Ivory Coast)'},
  {code: 'HR', name: 'Croatia (Hrvatska)'},
  {code: 'CU', name: 'Cuba'},
  {code: 'CY', name: 'Cyprus'},
  {code: 'CZ', name: 'Czech Republic'},
  {code: 'DK', name: 'Denmark'},
  {code: 'DJ', name: 'Djibouti'},
  {code: 'DM', name: 'Dominica'},
  {code: 'DO', name: 'Dominican Republic'},
  {code: 'TP', name: 'East Timor'},
  {code: 'EC', name: 'Ecuador'},
  {code: 'EG', name: 'Egypt'},
  {code: 'SV', name: 'El Salvador'},
  {code: 'GQ', name: 'Equatorial Guinea'},
  {code: 'ER', name: 'Eritrea'},
  {code: 'EE', name: 'Estonia'},
  {code: 'ET', name: 'Ethiopia'},
  {code: 'XA', name: 'External Territories of Australia'},
  {code: 'FK', name: 'Falkland Islands'},
  {code: 'FO', name: 'Faroe Islands'},
  {code: 'FJ', name: 'Fiji Islands'},
  {code: 'FI', name: 'Finland'},
  {code: 'FR', name: 'France'},
  {code: 'GF', name: 'French Guiana'},
  {code: 'PF', name: 'French Polynesia'},
  {code: 'TF', name: 'French Southern Territories'},
  {code: 'GA', name: 'Gabon'},
  {code: 'GM', name: 'Gambia The'},
  {code: 'GE', name: 'Georgia'},
  {code: 'DE', name: 'Germany'},
  {code: 'GH', name: 'Ghana'},
  {code: 'GI', name: 'Gibraltar'},
  {code: 'GR', name: 'Greece'},
  {code: 'GL', name: 'Greenland'},
  {code: 'GD', name: 'Grenada'},
  {code: 'GP', name: 'Guadeloupe'},
  {code: 'GU', name: 'Guam'},
  {code: 'GT', name: 'Guatemala'},
  {code: 'XU', name: 'Guernsey and Alderney'},
  {code: 'GN', name: 'Guinea'},
  {code: 'GW', name: 'Guinea-Bissau'},
  {code: 'GY', name: 'Guyana'},
  {code: 'HT', name: 'Haiti'},
  {code: 'HM', name: 'Heard and McDonald Islands'},
  {code: 'HN', name: 'Honduras'},
  {code: 'HK', name: 'Hong Kong S.A.R.'},
  {code: 'HU', name: 'Hungary'},
  {code: 'IS', name: 'Iceland'},
  {code: 'IN', name: 'India'},
  {code: 'ID', name: 'Indonesia'},
  {code: 'IR', name: 'Iran'},
  {code: 'IQ', name: 'Iraq'},
  {code: 'IE', name: 'Ireland'},
  {code: 'IL', name: 'Israel'},
  {code: 'IT', name: 'Italy'},
  {code: 'JM', name: 'Jamaica'},
  {code: 'JP', name: 'Japan'},
  {code: 'XJ', name: 'Jersey'},
  {code: 'JO', name: 'Jordan'},
  {code: 'KZ', name: 'Kazakhstan'},
  {code: 'KE', name: 'Kenya'},
  {code: 'KI', name: 'Kiribati'},
  {code: 'KP', name: 'Korea North'},
  {code: 'KR', name: 'Korea South'},
  {code: 'KW', name: 'Kuwait'},
  {code: 'KG', name: 'Kyrgyzstan'},
  {code: 'LA', name: 'Laos'},
  {code: 'LV', name: 'Latvia'},
  {code: 'LB', name: 'Lebanon'},
  {code: 'LS', name: 'Lesotho'},
  {code: 'LR', name: 'Liberia'},
  {code: 'LY', name: 'Libya'},
  {code: 'LI', name: 'Liechtenstein'},
  {code: 'LT', name: 'Lithuania'},
  {code: 'LU', name: 'Luxembourg'},
  {code: 'MO', name: 'Macau S.A.R.'},
  {code: 'MK', name: 'Macedonia'},
  {code: 'MG', name: 'Madagascar'},
  {code: 'MW', name: 'Malawi'},
  {code: 'MY', name: 'Malaysia'},
  {code: 'MV', name: 'Maldives'},
  {code: 'ML', name: 'Mali'},
  {code: 'MT', name: 'Malta'},
  {code: 'XM', name: 'Man (Isle of)'},
  {code: 'MH', name: 'Marshall Islands'},
  {code: 'MQ', name: 'Martinique'},
  {code: 'MR', name: 'Mauritania'},
  {code: 'MU', name: 'Mauritius'},
  {code: 'YT', name: 'Mayotte'},
  {code: 'MX', name: 'Mexico'},
  {code: 'FM', name: 'Micronesia'},
  {code: 'MD', name: 'Moldova'},
  {code: 'MC', name: 'Monaco'},
  {code: 'MN', name: 'Mongolia'},
  {code: 'MS', name: 'Montserrat'},
  {code: 'MA', name: 'Morocco'},
  {code: 'MZ', name: 'Mozambique'},
  {code: 'MM', name: 'Myanmar'},
  {code: 'NA', name: 'Namibia'},
  {code: 'NR', name: 'Nauru'},
  {code: 'NP', name: 'Nepal'},
  {code: 'AN', name: 'Netherlands Antilles'},
  {code: 'NL', name: 'Netherlands The'},
  {code: 'NC', name: 'New Caledonia'},
  {code: 'NZ', name: 'New Zealand'},
  {code: 'NI', name: 'Nicaragua'},
  {code: 'NE', name: 'Niger'},
  {code: 'NG', name: 'Nigeria'},
  {code: 'NU', name: 'Niue'},
  {code: 'NF', name: 'Norfolk Island'},
  {code: 'MP', name: 'Northern Mariana Islands'},
  {code: 'NO', name: 'Norway'},
  {code: 'OM', name: 'Oman'},
  {code: 'PK', name: 'Pakistan'},
  {code: 'PW', name: 'Palau'},
  {code: 'PS', name: 'Palestinian Territory Occupied'},
  {code: 'PA', name: 'Panama'},
  {code: 'PG', name: 'Papua new Guinea'},
  {code: 'PY', name: 'Paraguay'},
  {code: 'PE', name: 'Peru'},
  {code: 'PH', name: 'Philippines'},
  {code: 'PN', name: 'Pitcairn Island'},
  {code: 'PL', name: 'Poland'},
  {code: 'PT', name: 'Portugal'},
  {code: 'PR', name: 'Puerto Rico'},
  {code: 'QA', name: 'Qatar'},
  {code: 'RE', name: 'Reunion'},
  {code: 'RO', name: 'Romania'},
  {code: 'RU', name: 'Russia'},
  {code: 'RW', name: 'Rwanda'},
  {code: 'SH', name: 'Saint Helena'},
  {code: 'KN', name: 'Saint Kitts And Nevis'},
  {code: 'LC', name: 'Saint Lucia'},
  {code: 'PM', name: 'Saint Pierre and Miquelon'},
  {code: 'VC', name: 'Saint Vincent And The Grenadines'},
  {code: 'WS', name: 'Samoa'},
  {code: 'SM', name: 'San Marino'},
  {code: 'ST', name: 'Sao Tome and Principe'},
  {code: 'SA', name: 'Saudi Arabia'},
  {code: 'SN', name: 'Senegal'},
  {code: 'RS', name: 'Serbia'},
  {code: 'SC', name: 'Seychelles'},
  {code: 'SL', name: 'Sierra Leone'},
  {code: 'SG', name: 'Singapore'},
  {code: 'SK', name: 'Slovakia'},
  {code: 'SI', name: 'Slovenia'},
  {code: 'XG', name: 'Smaller Territories of the UK'},
  {code: 'SB', name: 'Solomon Islands'},
  {code: 'SO', name: 'Somalia'},
  {code: 'ZA', name: 'South Africa'},
  {code: 'GS', name: 'South Georgia'},
  {code: 'SS', name: 'South Sudan'},
  {code: 'ES', name: 'Spain'},
  {code: 'LK', name: 'Sri Lanka'},
  {code: 'SD', name: 'Sudan'},
  {code: 'SR', name: 'Suriname'},
  {code: 'SJ', name: 'Svalbard And Jan Mayen Islands'},
  {code: 'SZ', name: 'Swaziland'},
  {code: 'SE', name: 'Sweden'},
  {code: 'CH', name: 'Switzerland'},
  {code: 'SY', name: 'Syria'},
  {code: 'TW', name: 'Taiwan'},
  {code: 'TJ', name: 'Tajikistan'},
  {code: 'TZ', name: 'Tanzania'},
  {code: CountryIsoCodes.THAILAND, name: CountryNames.THAILAND},
  {code: 'TG', name: 'Togo'},
  {code: 'TK', name: 'Tokelau'},
  {code: 'TO', name: 'Tonga'},
  {code: 'TT', name: 'Trinidad And Tobago'},
  {code: 'TN', name: 'Tunisia'},
  {code: 'TR', name: 'Turkey'},
  {code: 'TM', name: 'Turkmenistan'},
  {code: 'TC', name: 'Turks And Caicos Islands'},
  {code: 'TV', name: 'Tuvalu'},
  {code: 'UG', name: 'Uganda'},
  {code: 'UA', name: 'Ukraine'},
  {code: 'AE', name: 'United Arab Emirates'},
  {code: CountryIsoCodes.UNITED_KINGDOM, name: CountryNames.UNITED_KINGDOM},
  {code: CountryIsoCodes.UNITED_STATES, name: CountryNames.UNITED_STATES},
  {code: CountryIsoCodes.UNITED_STATES_MINOR, name: CountryNames.UNITED_STATES_MINOR},
  {code: 'UY', name: 'Uruguay'},
  {code: 'UZ', name: 'Uzbekistan'},
  {code: 'VU', name: 'Vanuatu'},
  {code: 'VA', name: 'Vatican City State (Holy See)'},
  {code: 'VE', name: 'Venezuela'},
  {code: 'VN', name: 'Vietnam'},
  {code: 'VG', name: 'Virgin Islands (British)'},
  {code: 'VI', name: 'Virgin Islands (US)'},
  {code: 'WF', name: 'Wallis And Futuna Islands'},
  {code: 'EH', name: 'Western Sahara'},
  {code: 'YE', name: 'Yemen'},
  {code: 'YU', name: 'Yugoslavia'},
  {code: 'ZM', name: 'Zambia'},
  {code: 'ZW', name: 'Zimbabwe'}
]

export const CountryCodeMap: {[key: string]: string} = {}
  for (const countryCode of CountryCodes) {
    CountryCodeMap[countryCode.code] = countryCode.name;
}

interface StateWithCode {
  code?: string; // Two-letter State Code for US States, else blank
  name: string;
}

export const StateCodes: {[key: string]: StateWithCode[]} =
  {
    [CountryIsoCodes.UNITED_STATES]:
      [
        {code: 'AK', name: 'Alaska'},
        {code: 'AL', name: 'Alabama'},
        {code: 'AR', name: 'Arkansas'},
        {code: 'AZ', name: 'Arizona'},
        {code: 'CA', name: 'California'},
        {code: 'CO', name: 'Colorado'},
        {code: 'CT', name: 'Connecticut'},
        {code: 'DE', name: 'Delaware'},
        {code: 'FL', name: 'Florida'},
        {code: 'GA', name: 'Georgia'},
        {code: 'HI', name: 'Hawaii'},
        {code: 'IA', name: 'Iowa'},
        {code: 'ID', name: 'Idaho'},
        {code: 'IL', name: 'Illinois'},
        {code: 'IN', name: 'Indiana'},
        {code: 'KS', name: 'Kansas'},
        {code: 'KY', name: 'Kentucky'},
        {code: 'LA', name: 'Louisiana'},
        {code: 'MA', name: 'Massachusetts'},
        {code: 'MD', name: 'Maryland'},
        {code: 'ME', name: 'Maine'},
        {code: 'MI', name: 'Michigan'},
        {code: 'MN', name: 'Minnesota'},
        {code: 'MO', name: 'Missouri'},
        {code: 'MS', name: 'Mississippi'},
        {code: 'MT', name: 'Montana'},
        {code: 'NC', name: 'North Carolina'},
        {code: 'ND', name: 'North Dakota'},
        {code: 'NE', name: 'Nebraska'},
        {code: 'NH', name: 'New Hampshire'},
        {code: 'NJ', name: 'New Jersey'},
        {code: 'NM', name: 'New Mexico'},
        {code: 'NV', name: 'Nevada'},
        {code: 'NY', name: 'New York'},
        {code: 'OH', name: 'Ohio'},
        {code: 'OK', name: 'Oklahoma'},
        {code: 'OR', name: 'Oregon'},
        {code: 'PA', name: 'Pennsylvania'},
        {code: 'RI', name: 'Rhode Island'},
        {code: 'SC', name: 'South Carolina'},
        {code: 'SD', name: 'South Dakota'},
        {code: 'TN', name: 'Tennessee'},
        {code: 'TX', name: 'Texas'},
        {code: 'UT', name: 'Utah'},
        {code: 'VA', name: 'Virginia'},
        {code: 'VT', name: 'Vermont'},
        {code: 'WA', name: 'Washington'},
        {code: 'WI', name: 'Wisconsin'},
        {code: 'WV', name: 'West Virginia'},
        {code: 'WY', name: 'Wyoming'},
        {code: 'DC', name: 'District of Columbia'},
      ],
    [CountryIsoCodes.UNITED_STATES_MINOR]:
      [
        {code: 'AS', name: 'American Samoa'},
        {code: 'GU', name: 'Guam'},
        {code: 'MP', name: 'Northern Mariana Islands'},
        {code: 'PR', name: 'Puerto Rico'},
        {code: 'VI', name: 'U.S. Virgin Islands'}
      ],
    [CountryIsoCodes.CANADA]:
      [
        {code: 'Alberta', name: 'Alberta'},
        {code: 'British Columbia', name: 'British Columbia'},
        {code: 'Manitoba', name: 'Manitoba'},
        {code: 'Newfoundland and Labrador', name: 'Newfoundland and Labrador'},
        {code: 'New Brunswick', name: 'New Brunswick'},
        {code: 'Northwest Territoires', name: 'Northwest Territoires'},
        {code: 'Nova Scotia', name: 'Nova Scotia'},
        {code: 'Nunavut', name: 'Nunavut'},
        {code: 'Ontario', name: 'Ontario'},
        {code: 'Prince Edward Island', name: 'Prince Edward Island'},
        {code: 'Quebec', name: 'Quebec'},
        {code: 'Saskatchewan', name: 'Saskatchewan'},
        {code: 'Yukon', name: 'Yukon'},
      ],
    [CountryIsoCodes.AUSTRALIA]:
      [
        {code: 'Auckland', name: 'Auckland',},
        {code: 'Australian Capital Territory', name: 'Australian Capital Territory',},
        {code: 'Balgowlah', name: 'Balgowlah',},
        {code: 'Balmain', name: 'Balmain',},
        {code: 'Bankstown', name: 'Bankstown',},
        {code: 'Baulkham Hills', name: 'Baulkham Hills',},
        {code: 'Bonnet Bay', name: 'Bonnet Bay',},
        {code: 'Camberwell', name: 'Camberwell',},
        {code: 'Carole Park', name: 'Carole Park',},
        {code: 'Castle Hill', name: 'Castle Hill',},
        {code: 'Caulfield', name: 'Caulfield',},
        {code: 'Chatswood', name: 'Chatswood',},
        {code: 'Cheltenham', name: 'Cheltenham',},
        {code: 'Cherrybrook', name: 'Cherrybrook',},
        {code: 'Clayton', name: 'Clayton',},
        {code: 'Collingwood', name: 'Collingwood',},
        {code: 'Frenchs Forest', name: 'Frenchs Forest',},
        {code: 'Hawthorn', name: 'Hawthorn',},
        {code: 'Jannnali', name: 'Jannnali',},
        {code: 'Knoxfield', name: 'Knoxfield',},
        {code: 'Melbourne', name: 'Melbourne',},
        {code: 'New South Wales', name: 'New South Wales',},
        {code: 'Northern Territory', name: 'Northern Territory',},
        {code: 'Perth', name: 'Perth',},
        {code: 'Queensland', name: 'Queensland',},
        {code: 'South Australia', name: 'South Australia',},
        {code: 'Tasmania', name: 'Tasmania',},
        {code: 'Templestowe', name: 'Templestowe',},
        {code: 'Victoria', name: 'Victoria',},
        {code: 'Werribee south', name: 'Werribee south',},
        {code: 'Western Australia', name: 'Western Australia',},
        {code: 'Wheeler', name: 'Wheeler',},
      ],
    [CountryIsoCodes.UNITED_KINGDOM]:
      [
        {code: 'Aberdeen', name: 'Aberdeen'},
        {code: 'Aberdeenshire', name: 'Aberdeenshire'},
        {code: 'Argyll', name: 'Argyll'},
        {code: 'Armagh', name: 'Armagh'},
        {code: 'Bedfordshire', name: 'Bedfordshire'},
        {code: 'Belfast', name: 'Belfast'},
        {code: 'Berkshire', name: 'Berkshire'},
        {code: 'Birmingham', name: 'Birmingham'},
        {code: 'Brechin', name: 'Brechin'},
        {code: 'Bridgnorth', name: 'Bridgnorth'},
        {code: 'Bristol', name: 'Bristol'},
        {code: 'Buckinghamshire', name: 'Buckinghamshire'},
        {code: 'Cambridge', name: 'Cambridge'},
        {code: 'Cambridgeshire', name: 'Cambridgeshire'},
        {code: 'Channel Islands', name: 'Channel Islands'},
        {code: 'Cheshire', name: 'Cheshire'},
        {code: 'Cleveland', name: 'Cleveland'},
        {code: 'Co Fermanagh', name: 'Co Fermanagh'},
        {code: 'Conwy', name: 'Conwy'},
        {code: 'Cornwall', name: 'Cornwall'},
        {code: 'Coventry', name: 'Coventry'},
        {code: 'Craven Arms', name: 'Craven Arms'},
        {code: 'Cumbria', name: 'Cumbria'},
        {code: 'Denbighshire', name: 'Denbighshire'},
        {code: 'Derby', name: 'Derby'},
        {code: 'Derbyshire', name: 'Derbyshire'},
        {code: 'Devon', name: 'Devon'},
        {code: 'Dial Code Dungannon', name: 'Dial Code Dungannon'},
        {code: 'Didcot', name: 'Didcot'},
        {code: 'Dorset', name: 'Dorset'},
        {code: 'Dunbartonshire', name: 'Dunbartonshire'},
        {code: 'Durham', name: 'Durham'},
        {code: 'East Dunbartonshire', name: 'East Dunbartonshire'},
        {code: 'East Lothian', name: 'East Lothian'},
        {code: 'East Midlands', name: 'East Midlands'},
        {code: 'East Sussex', name: 'East Sussex'},
        {code: 'East Yorkshire', name: 'East Yorkshire'},
        {code: 'England', name: 'England'},
        {code: 'Essex', name: 'Essex'},
        {code: 'Fermanagh', name: 'Fermanagh'},
        {code: 'Fife', name: 'Fife'},
        {code: 'Flintshire', name: 'Flintshire'},
        {code: 'Fulham', name: 'Fulham'},
        {code: 'Gainsborough', name: 'Gainsborough'},
        {code: 'Glocestershire', name: 'Glocestershire'},
        {code: 'Gwent', name: 'Gwent'},
        {code: 'Hampshire', name: 'Hampshire'},
        {code: 'Hants', name: 'Hants'},
        {code: 'Herefordshire', name: 'Herefordshire'},
        {code: 'Hertfordshire', name: 'Hertfordshire'},
        {code: 'Ireland', name: 'Ireland'},
        {code: 'Isle Of Man', name: 'Isle Of Man'},
        {code: 'Isle of Wight', name: 'Isle of Wight'},
        {code: 'Kenford', name: 'Kenford'},
        {code: 'Kent', name: 'Kent'},
        {code: 'Kilmarnock', name: 'Kilmarnock'},
        {code: 'Lanarkshire', name: 'Lanarkshire'},
        {code: 'Lancashire', name: 'Lancashire'},
        {code: 'Leicestershire', name: 'Leicestershire'},
        {code: 'Lincolnshire', name: 'Lincolnshire'},
        {code: 'Llanymynech', name: 'Llanymynech'},
        {code: 'London', name: 'London'},
        {code: 'Ludlow', name: 'Ludlow'},
        {code: 'Manchester', name: 'Manchester'},
        {code: 'Mayfair', name: 'Mayfair'},
        {code: 'Merseyside', name: 'Merseyside'},
        {code: 'Mid Glamorgan', name: 'Mid Glamorgan'},
        {code: 'Middlesex', name: 'Middlesex'},
        {code: 'Mildenhall', name: 'Mildenhall'},
        {code: 'Monmouthshire', name: 'Monmouthshire'},
        {code: 'Newton Stewart', name: 'Newton Stewart'},
        {code: 'Norfolk', name: 'Norfolk'},
        {code: 'North Humberside', name: 'North Humberside'},
        {code: 'North Yorkshire', name: 'North Yorkshire'},
        {code: 'Northamptonshire', name: 'Northamptonshire'},
        {code: 'Northants', name: 'Northants'},
        {code: 'Northern Ireland', name: 'Northern Ireland'},
        {code: 'Northumberland', name: 'Northumberland'},
        {code: 'Nottinghamshire', name: 'Nottinghamshire'},
        {code: 'Oxford', name: 'Oxford'},
        {code: 'Powys', name: 'Powys'},
        {code: 'Roos-shire', name: 'Roos-shire'},
        {code: 'SUSSEX', name: 'SUSSEX'},
        {code: 'Sark', name: 'Sark'},
        {code: 'Scotland', name: 'Scotland'},
        {code: 'Scottish Borders', name: 'Scottish Borders'},
        {code: 'Shropshire', name: 'Shropshire'},
        {code: 'Somerset', name: 'Somerset'},
        {code: 'South Glamorgan', name: 'South Glamorgan'},
        {code: 'South Wales', name: 'South Wales'},
        {code: 'South Yorkshire', name: 'South Yorkshire'},
        {code: 'Southwell', name: 'Southwell'},
        {code: 'Staffordshire', name: 'Staffordshire'},
        {code: 'Strabane', name: 'Strabane'},
        {code: 'Suffolk', name: 'Suffolk'},
        {code: 'Surrey', name: 'Surrey'},
        {code: 'Sussex', name: 'Sussex'},
        {code: 'Twickenham', name: 'Twickenham'},
        {code: 'Tyne and Wear', name: 'Tyne and Wear'},
        {code: 'Tyrone', name: 'Tyrone'},
        {code: 'Utah', name: 'Utah'},
        {code: 'Wales', name: 'Wales'},
        {code: 'Warwickshire', name: 'Warwickshire'},
        {code: 'West Lothian', name: 'West Lothian'},
        {code: 'West Midlands', name: 'West Midlands'},
        {code: 'West Sussex', name: 'West Sussex'},
        {code: 'West Yorkshire', name: 'West Yorkshire'},
        {code: 'Whissendine', name: 'Whissendine'},
        {code: 'Wiltshire', name: 'Wiltshire'},
        {code: 'Wokingham', name: 'Wokingham'},
        {code: 'Worcestershire', name: 'Worcestershire'},
        {code: 'Wrexham', name: 'Wrexham'},
        {code: 'Wurttemberg', name: 'Wurttemberg'},
        {code: 'Yorkshire', name: 'Yorkshire'},
      ],
    [CountryIsoCodes.THAILAND]:
      [
        // TODO
      ],
  }

export const SavedViewUrlParamName = 'saved_view_id';

/**
 * Strips document-level tags (<meta>, <base>, <title>, <link>, <script>) from a
 * full HTML document string, keeping <style> blocks and the body content. Used
 * before rendering server-generated email HTML inline (see IsolatedHtmlComponent):
 * Shadow DOM scopes <style> but does NOT neutralise <meta name="viewport">, which
 * Chrome may apply to the whole page and collapse its layout viewport.
 */
export function isolateDocumentHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  doc.querySelectorAll('meta, base, title, link, script').forEach(el => el.remove());
  const headStyles = Array.from(doc.head.querySelectorAll('style'))
    .map(style => style.outerHTML)
    .join('');
  return headStyles + doc.body.innerHTML;
}

/** A day-of-month (1-31) with its ordinal suffix, e.g. 1 -> "1st", 22 -> "22nd". */
export function ordinalSuffixedDay(day: number): string {
  const remainder10 = day % 10;
  const remainder100 = day % 100;
  if ((remainder10 === 1) && (remainder100 !== 11)) { return day + 'st'; }
  if ((remainder10 === 2) && (remainder100 !== 12)) { return day + 'nd'; }
  if ((remainder10 === 3) && (remainder100 !== 13)) { return day + 'rd'; }
  return day + 'th';
}
